import React, { useMemo } from 'react'

import {
  Alert,
  Box,
  Stack,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import {
  DataGrid,
  TColumnDef,
  Typography,
  Button,
  Form,
  useModal,
  Pagination,
  Icon,
  Tag,
  Select,
  IOptionProps,
  ISelectProps,
} from '@pnld/components-web'

import DateRangeFilter from '@/components/DateRange'

import { STATUS_SHORT_LABEL, STATUS_VARIANT_MAP } from './constants'
import { useSendingsController } from './controller'
import SendDataModal from './SendDataModal'
import {
  TableSkeleton,
  PaginationSkeleton,
  EmptyState,
} from './skeletonSendings'
import { ISendings } from './types'

type TSelectWithOnChange = React.ComponentType<
  ISelectProps & { onChange?: (option: IOptionProps | null) => void }
>
const SelectField = Select as TSelectWithOnChange

const Sendings = () => {
  const { isOpen, onClose, onOpen } = useModal()
  const {
    tableData,
    handleSearchChange,
    handleStartDateChange,
    handleFinalDateChange,
    handleUserChange,
    handleRefresh,
    userOptions,
    isLoading,
    isFetching,
    processingPrediction,
    ...paginationProps
  } = useSendingsController({ onClose })

  const columns = useMemo<Array<TColumnDef<ISendings>>>(
    () => [
      {
        accessorKey: 'filename',
        header: 'Nome do arquivo',
        enableSorting: false,
        cell: ({ row }) => row.original.filename ?? '—',
      },
      {
        accessorKey: 'username',
        header: 'Nome do usuário',
        enableSorting: false,
        cell: ({ row }) => row.original.username ?? '—',
      },
      {
        accessorKey: 'censusYear',
        header: 'Ano do censo',
        enableSorting: false,
        cell: ({ row }) => row.original.censusYear ?? '—',
      },
      {
        accessorKey: 'predictionYear',
        header: 'Ano de predição',
        enableSorting: false,
        cell: ({ row }) => row.original.predictionYear ?? '—',
      },
      {
        accessorFn: row => row.sendDate,
        header: 'Data de envio',
        enableSorting: false,
      },
      {
        accessorKey: 'statusDisplay',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <Tooltip label={row.original.statusDisplay} placement="top">
            <Box display="inline-block">
              <Tag
                variant={
                  STATUS_VARIANT_MAP[row.original.statusCode] ?? 'neutral'
                }
              >
                {STATUS_SHORT_LABEL[row.original.statusCode] ??
                  row.original.statusDisplay}
              </Tag>
            </Box>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'resultMessage',
        header: 'Resultado',
        enableSorting: false,
        cell: ({ row }) => {
          const msg = row.original.resultMessage
          if (!msg) return '—'
          return (
            <Tooltip label={msg} placement="top">
              <Box
                maxW="200px"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                cursor="default"
              >
                {msg}
              </Box>
            </Tooltip>
          )
        },
      },
    ],
    []
  )

  return (
    <Stack
      flexGrow={1}
      flex={1}
      p={5}
      boxShadow="md"
      borderRadius="base"
      gap={4}
      h="100%"
    >
      <Typography variant="h-xs" color="brand.primary.dark_1">
        Histórico de Envios de Predições
      </Typography>

      {processingPrediction && (
        <Alert
          status="warning"
          w="full"
          maxW="unset"
          gap={4}
          mt="0 !important"
          py="3xs"
        >
          <Icon name="exclamation-triangle" size="xs" variant="highlight" />
          <Stack color="black">
            <Text fontWeight="bold">Temos uma predição em andamento!</Text>
            <Text mt="0 !important">
              Em até 48h os dados da predição estarão disponíveis.
            </Text>
          </Stack>
        </Alert>
      )}
      <Form onSubmit={() => {}}>
        <Stack gap={3}>
          <Flex gap={4} justify="space-between">
            <InputGroup maxW="400px">
              <Input
                placeholder="Buscar por nome do arquivo"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleSearchChange(e.target.value)
                }
              />
              <InputRightElement>
                <Icon name="search" size="2xs" />
              </InputRightElement>
            </InputGroup>
            <Flex gap={2}>
              {processingPrediction && (
                <Button
                  label="Atualizar"
                  variant="secondary"
                  iconName="refresh"
                  onClick={handleRefresh}
                  disabled={isFetching}
                />
              )}
              <Button
                label="Enviar dados"
                variant="secondary"
                iconName="upload"
                onClick={onOpen}
                disabled={processingPrediction}
              />
            </Flex>
          </Flex>

          <Flex align="center" gap={4} w="full">
            <Typography
              variant="b-sm"
              color="brand.primary.dark_1"
              whiteSpace="nowrap"
            >
              Filtrar por
            </Typography>
            <Box flex={1} minW="200px" maxW="340px">
              <SelectField
                name="coUser"
                placeholder="Usuário"
                options={userOptions}
                isClearable
                onChange={(option: IOptionProps | null) =>
                  handleUserChange(option ? Number(option.value) : undefined)
                }
              />
            </Box>
            <Flex align="center" gap={2} flex={1} minW={0}>
              <Typography
                variant="b-sm"
                color="brand.primary.dark_1"
                whiteSpace="nowrap"
              >
                Data de envio
              </Typography>
              <Box flex={1} minW={0}>
                <DateRangeFilter
                  onStartDateChange={handleStartDateChange}
                  onFinalDateChange={handleFinalDateChange}
                />
              </Box>
            </Flex>
          </Flex>
        </Stack>
      </Form>
      <Box overflowX="auto" flexGrow={1} h="100%">
        {isLoading || isFetching ? (
          <TableSkeleton />
        ) : tableData.length === 0 ? (
          <EmptyState />
        ) : (
          <DataGrid columns={columns} data={tableData} hasPagination={false} />
        )}
      </Box>
      {isLoading ? (
        <PaginationSkeleton />
      ) : tableData.length > 0 ? (
        <Pagination {...paginationProps} />
      ) : null}
      <SendDataModal isOpen={isOpen} onClose={onClose} />
    </Stack>
  )
}

export default Sendings
